import { Suspense } from 'react';
import { useSelectedEnvironmentKey } from '@gonfalon/app-context/useSelectedEnvironmentKey';
import { enableRatioMetrics, metricWindowingFrontend } from '@gonfalon/dogfood-flags/dogfood-flags';
import { ErrorBoundary } from '@gonfalon/error-boundaries/ErrorBoundary';
import { CopyToClipboard } from '@gonfalon/launchpad-experimental/CopyToClipboard';
import { TextTruncator } from '@gonfalon/launchpad-experimental/TextTruncator';
import { TextTruncatorWithTooltip } from '@gonfalon/launchpad-experimental/TextTruncatorWithTooltip';
import { Sources } from '@gonfalon/metric-events/Sources';
import { toMetric } from '@gonfalon/navigator/navigators';
import type { GetMetricQueryParams } from '@gonfalon/openapi/operations/getMetric';
import type { DependentReleasePolicyRep } from '@gonfalon/openapi/types';
import { useProjectKey } from '@gonfalon/router/useProjectKey';
import { Button, LinkButton, ProgressBar, Tag, TagGroup, TagList } from '@launchpad-ui/components';
import { Icon } from '@launchpad-ui/icons';
import { clsx } from 'clsx';

import { metricsSearchTracker } from '../analytics';
import { isLDHosted } from '../isLDHosted';
import { MetricDataSourceBadge } from '../MetricDataSources';
import { MetricDefinition, metricDefinitionIncludesSuccessCriteria } from '../MetricDefinition';
import { MetricEventDefault } from '../MetricEventDefault';
import type { MetricKind } from '../MetricKind';
import { MetricLabel } from '../MetricLabel';
import { MetricLastSeenInfo } from '../MetricLastSeenInfo';
import { MetricMeasurementWindow } from '../MetricMeasurementWindow';
import { MetricSuccessCriteria } from '../MetricSuccessCriteria';
import { hasWinsorization, MetricWinsorization } from '../MetricWinsorization';
import type { Metric } from '../types';
import { useSuspenseMetric } from '../useSuspenseMetric';

import { trackMetricSelectionLinkToFullResourceClicked } from './analytics';

import styles from './MetricDetailsPanel.module.css';

interface MetricDetailsPanelProps {
  metric: Metric;
  unavailableReason?: string;
  scopeByEnvironment?: boolean;
}

// Component to render metric sources (similar to MetricSidebar)
function MetricSourcesSection({ metric, projectKey }: { metric: Metric; projectKey: string }) {
  const sources =
    metric.dataSource && !isLDHosted(metric.dataSource?.key) ? [metric.dataSource.key] : (metric.eventSources ?? []);

  if (metric.dataSource && metric.dataSource?.key && !isLDHosted(metric.dataSource.key)) {
    return (
      <MetricDataSourceBadge
        metricDataSource={{
          name: metric.dataSource?._name ?? '',
          integrationKey: metric.dataSource._integrationKey ?? '',
          key: metric.dataSource?.key ?? '',
          _projectSummary: { key: projectKey },
        }}
      />
    );
  }

  if (sources.length > 0) {
    return <Sources sources={sources.filter((src) => src !== undefined)} variant="small" />;
  }

  return <span>No Sources Found</span>;
}

// Internal component that fetches full metric data
function MetricDetailsPanelInternal({
  metric,
  projectKey,
  unavailableReason,
  scopeByEnvironment,
}: {
  metric: Metric;
  projectKey: string;
  unavailableReason?: string;
  scopeByEnvironment?: boolean;
}) {
  const selectedEnvironmentKey = useSelectedEnvironmentKey();
  const isRatioMetricsEnabled = enableRatioMetrics();
  const isMetricWindowingEnabled = metricWindowingFrontend();

  const metricQuery: GetMetricQueryParams = {
    expand: ['experiments', 'eventSources', 'guardedRolloutCount', 'releasePolicies', 'releasePolicyCount'],
  };
  const { data: fullMetric } = useSuspenseMetric(projectKey, metric.key, metricQuery, {
    staleTime: 5 * 60 * 1000,
  });

  const path = toMetric({ projectKey, metricKey: metric.key });

  // Grouped under the event key when there is one, otherwise shown under the definition
  // so trace metrics (filters, no event key) don't lose the indicator.
  const filtersIndicator = !!fullMetric.filters && (
    <span className={styles.filtersApplied}>
      <Icon name="filter-list" size="tiny" aria-hidden="true" />
      Event filters applied
    </span>
  );

  return (
    <div className={styles.panelContainer}>
      <div className={styles.detailsPanel}>
        {/* Last seen with environment */}
        <div className={styles.section}>
          <TextTruncatorWithTooltip tooltipContent={fullMetric.name} className={styles.metricName} maxLines={2}>
            {fullMetric.name}
          </TextTruncatorWithTooltip>
          <MetricLastSeenInfo
            metric={fullMetric}
            fallback="Not seen recently"
            showEnvironment
            scopedToEnvironment={scopeByEnvironment ? selectedEnvironmentKey : undefined}
          />
          <MetricLabel
            kind={fullMetric.kind as MetricKind}
            isNumeric={fullMetric.isNumeric}
            unitAggregationType={fullMetric.unitAggregationType}
          />
        </div>

        {unavailableReason && (
          <div className={styles.unavailableReason}>
            <div className={styles.unavailableReasonTitle}>
              <Icon size="tiny" name="info-circle-outline" className={styles.unavailableReasonIcon} /> Unavailable for
              selection
            </div>
            {unavailableReason}
          </div>
        )}

        {/* Metric structure */}
        {isRatioMetricsEnabled && (
          <div className={styles.section}>
            <h4 className={styles.sectionHeader}>Metric structure</h4>
            <div className={styles.sectionContent}>
              <TagGroup aria-label="Metric structure">
                <TagList>
                  <Tag>{fullMetric.denominator ? 'Ratio' : 'Simple'}</Tag>
                </TagList>
              </TagGroup>
            </div>
          </div>
        )}

        {/* Description */}
        {fullMetric.description && (
          <div className={styles.section}>
            <h4 className={styles.sectionHeader}>Description</h4>
            <p className={styles.sectionContent}>{fullMetric.description}</p>
          </div>
        )}

        {/* Definition */}
        <div className={styles.section}>
          <h4 className={styles.sectionHeader}>Definition</h4>
          <div className={styles.sectionContent}>
            <MetricDefinition className={styles.metricDefinition} metric={fullMetric} />
            {!fullMetric.eventKey && filtersIndicator}
          </div>
        </div>

        {/* Event key */}
        {fullMetric.eventKey && (
          <div className={styles.section}>
            <h4 className={styles.sectionHeader}>Event key</h4>
            <div className={clsx(styles.sectionContent, styles.eventKeyContent)}>
              <CopyToClipboard text={fullMetric.eventKey} tooltip="Copy event key">
                <Button
                  size="small"
                  data-test-id="event-key"
                  aria-label={`Copy ${fullMetric.eventKey} to your clipboard.`}
                  className={styles.eventKey}
                >
                  <TextTruncator>{fullMetric.eventKey}</TextTruncator>
                  <Icon name="copy-clipboard" size="small" />
                </Button>
              </CopyToClipboard>
              {filtersIndicator}
            </div>
          </div>
        )}

        {/* Units without events */}
        {!!fullMetric.eventDefault && (
          <div className={styles.section}>
            <h4 className={styles.sectionHeader}>Units without {fullMetric.kind === 'trace' ? 'spans' : 'events'}</h4>
            <div className={styles.sectionContent}>
              <MetricEventDefault metric={fullMetric} />
            </div>
          </div>
        )}

        {/* Success criteria */}
        {(!!fullMetric.successCriteria || fullMetric.kind === 'click' || fullMetric.kind === 'pageview') &&
          !metricDefinitionIncludesSuccessCriteria(fullMetric) && (
            <div className={styles.section}>
              <h4 className={styles.sectionHeader}>Success criteria</h4>
              <div className={styles.sectionContent}>
                <MetricSuccessCriteria metric={fullMetric} />
              </div>
            </div>
          )}

        {/* Measurement window */}
        {/* MetricMeasurementWindow returns null when windowStartOffset is non-zero but windowEndOffset is absent */}
        {isMetricWindowingEnabled &&
          !(
            fullMetric.windowStartOffset !== undefined &&
            fullMetric.windowStartOffset !== 0 &&
            fullMetric.windowEndOffset === undefined
          ) && (
            <div className={styles.section}>
              <h4 className={styles.sectionHeader}>Measurement window</h4>
              <div className={styles.sectionContent}>
                <MetricMeasurementWindow
                  windowStartOffset={fullMetric.windowStartOffset}
                  windowEndOffset={fullMetric.windowEndOffset}
                />
              </div>
            </div>
          )}

        {/* Winsorization  */}
        {hasWinsorization(fullMetric) && (
          <div className={styles.section}>
            <h4 className={styles.sectionHeader}>Winsorization</h4>
            <div className={styles.sectionContent}>
              <MetricWinsorization metric={fullMetric} />
            </div>
          </div>
        )}

        {/* Sources */}
        <div className={styles.section}>
          <h4 className={styles.sectionHeader}>Source</h4>
          <div className={styles.sectionContent}>
            <MetricSourcesSection metric={fullMetric} projectKey={projectKey} />
          </div>
        </div>

        {/* Release policies */}
        {fullMetric.releasePolicies && fullMetric.releasePolicies.length > 0 && (
          <div className={styles.section}>
            <h4 className={styles.sectionHeader}>Release policies</h4>
            <div className={clsx(styles.sectionContent, styles.releasePolicyContent)}>
              {fullMetric.releasePolicies.map((policy: DependentReleasePolicyRep) => (
                <span key={policy.key} className={styles.releasePolicyItem}>
                  <TextTruncatorWithTooltip tooltipContent={policy.name}>{policy.name}</TextTruncatorWithTooltip>
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
      <div className={styles.linkButtonWrapper}>
        <LinkButton
          href={path}
          target="_blank"
          className={styles.linkButton}
          aria-label="View metric details"
          onPress={() => {
            const attributes = { kind: 'metric' } as const;
            trackMetricSelectionLinkToFullResourceClicked(attributes);
            metricsSearchTracker.track('Link to Full Resource Clicked', attributes);
          }}
        >
          <Icon name="link-external" size="tiny" />
        </LinkButton>
      </div>
    </div>
  );
}

export function MetricDetailsPanel({ metric, unavailableReason, scopeByEnvironment }: MetricDetailsPanelProps) {
  const projectKey = useProjectKey();

  return (
    <ErrorBoundary
      severity="medium"
      fallbackRender={() => (
        <div className={styles.errorContainer}>
          <span className={styles.errorMessage}>
            <Icon name="warning" className={styles.errorIcon} />
            Failed to load metric details
          </span>
        </div>
      )}
    >
      <Suspense
        fallback={
          <div className={styles.loadingContainer}>
            <ProgressBar size="small" isIndeterminate aria-label="Loading metric details" />
          </div>
        }
      >
        <MetricDetailsPanelInternal
          metric={metric}
          projectKey={projectKey}
          unavailableReason={unavailableReason}
          scopeByEnvironment={scopeByEnvironment}
        />
      </Suspense>
    </ErrorBoundary>
  );
}
